"use client"

import React from 'react';
import { usePathname } from 'next/navigation';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { getBranchCodeFromPath, getBranchData, getCurrentBranch } from '@/lib/config/branches-config';

interface ServiceIncludesProps {
  className?: string;
  includeData?: {
    title?: string;
    description?: string;
  } | null;
  docsData?: {
    title?: string;
    description?: string;
    items?: Array<{
      title?: string;
      description?: string;
    }>;
  } | null;
  techDocs?: Array<{
    src: string;
    name: string;
  }> | null;
}

export default function ServiceIncludes({ className, includeData, docsData, techDocs }: ServiceIncludesProps) {
  const pathname = usePathname() || '/';
  const branchCode = getBranchCodeFromPath(pathname);
  const isMoscow = branchCode === 'msk';
  // Телефон был захардкожен в обеих ветках и расходился с branches-config.
  // Для локального SEO NAP обязан совпадать с тем, что уходит в LocalBusiness.
  const branch = getBranchData(branchCode) || getCurrentBranch();
  const branchPhone = branch?.phone || '';
  const branchPhoneHref = branchPhone.replace(/[^+\d]/g, '');
  
  const hasIncludes = Boolean(includeData?.description);
  const docsItems = Array.isArray(docsData?.items) ? docsData?.items : [];
  const hasDocs = docsItems.length > 0 && docsItems.some((x) => Boolean(x?.description));
  const techDocsItems = Array.isArray(techDocs) ? techDocs : [];
  const hasTechDocs = techDocsItems.length > 0 && techDocsItems.some((x) => Boolean(x?.src) && Boolean(x?.name));

  // Если нет данных, не показываем компонент
  if (!hasIncludes && !hasDocs && !hasTechDocs) {
    return null;
  }

  const defaultTab = hasIncludes ? 'includes' : hasDocs ? 'docs' : hasTechDocs ? 'tech-docs' : 'guarantee';
  
  return (
    <section className={`py-4 sm:py-8 ${className || ''}`}>
      <div className="w-full">
        <Tabs defaultValue={defaultTab} className="w-full">
          <TabsList
            className="w-full flex flex-nowrap items-center justify-start gap-1 sm:gap-2 h-auto p-1 sm:p-1.5 bg-[#0d223c] text-white underline outline-none dark:bg-[#0d223c] dark:text-white overflow-x-auto scrollbar-hide"
          >
            {hasIncludes && (
              <TabsTrigger
                value="includes"
                className="shrink-0 whitespace-nowrap text-xs sm:text-sm px-2 sm:px-4 py-2 sm:py-2.5 data-[state=active]:!bg-[#d30000] data-[state=active]:!text-white data-[state=active]:!shadow-sm"
              >
                {includeData?.title || 'Что входит в услугу'}
              </TabsTrigger>
            )}
            {hasDocs && (
              <TabsTrigger
                value="docs"
                className="shrink-0 whitespace-nowrap text-xs sm:text-sm px-2 sm:px-4 py-2 sm:py-2.5 data-[state=active]:!bg-[#d30000] data-[state=active]:!text-white data-[state=active]:!shadow-sm"
              >
                Документы
              </TabsTrigger>
            )}
            {hasTechDocs && (
              <TabsTrigger
                value="tech-docs"
                className="shrink-0 whitespace-nowrap text-xs sm:text-sm px-2 sm:px-4 py-2 sm:py-2.5 data-[state=active]:!bg-[#d30000] data-[state=active]:!text-white data-[state=active]:!shadow-sm"
              >
                Техническая документация
              </TabsTrigger>
            )}
            <TabsTrigger 
              value="guarantee" 
              className="shrink-0 whitespace-nowrap text-xs sm:text-sm px-2 sm:px-4 py-2 sm:py-2.5 data-[state=active]:!bg-[#d30000] data-[state=active]:!text-white data-[state=active]:!shadow-sm"
            >
              Гарантии
            </TabsTrigger>
            <TabsTrigger 
              value="delivery" 
              className="shrink-0 whitespace-nowrap text-xs sm:text-sm px-2 sm:px-4 py-2 sm:py-2.5 data-[state=active]:!bg-[#d30000] data-[state=active]:!text-white data-[state=active]:!shadow-sm"
            >
              Доставка
            </TabsTrigger>
            <TabsTrigger 
              value="payment" 
              className="shrink-0 whitespace-nowrap text-xs sm:text-sm px-2 sm:px-4 py-2 sm:py-2.5 data-[state=active]:!bg-[#d30000] data-[state=active]:!text-white data-[state=active]:!shadow-sm"
            >
              Оплата
            </TabsTrigger>
          </TabsList>

          {hasIncludes && (
            <TabsContent value="includes" className="mt-4 sm:mt-6">
              <div
                className="rounded-lg border border-slate-200 bg-white p-4 sm:p-6 shadow-sm"
                dangerouslySetInnerHTML={{ __html: includeData?.description || '' }}
              />
            </TabsContent>
          )}

          {hasDocs && (
            <TabsContent value="docs" className="mt-4 sm:mt-6">
              <div className="rounded-lg border border-slate-200 bg-white p-4 sm:p-6 shadow-sm">
                <div className="[&_p]:mb-4 [&_p:last-child]:mb-0 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:my-4 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:my-4 [&_li]:mt-1">
                  {docsItems
                    .filter((x) => Boolean(x?.description))
                    .map((item, idx) => (
                      <div
                        key={`${item?.title || 'item'}-${idx}`}
                        className={idx === 0 ? '' : 'mt-6 pt-6 border-t border-slate-200'}
                        dangerouslySetInnerHTML={{ __html: item?.description || '' }}
                      />
                    ))}
                </div>
              </div>
            </TabsContent>
          )}

          {hasTechDocs && (
            <TabsContent value="tech-docs" className="mt-4 sm:mt-6">
              <div className="rounded-lg border border-slate-200 bg-white p-4 sm:p-6 shadow-sm">
                <ul className="space-y-2">
                  {techDocsItems
                    .filter((x) => Boolean(x?.src) && Boolean(x?.name))
                    .map((doc) => (
                      <li key={doc.src}>
                        <a
                          href={doc.src}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 hover:underline break-all"
                        >
                          {doc.name}
                        </a>
                      </li>
                    ))}
                </ul>
              </div>
            </TabsContent>
          )}

          <TabsContent value="guarantee" className="mt-4 sm:mt-6">
            <div className="rounded-lg border border-slate-200 bg-white p-4 sm:p-6 shadow-sm">
              <ul className="space-y-3 sm:space-y-4">
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-purple-100 flex items-center justify-center mt-0.5">
                    <svg className="w-3 h-3 sm:w-4 sm:h-4 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                  </span>
                  <span className="text-sm sm:text-base text-gray-700">На весь товар и изделия предоставляется гарантия <strong className="text-gray-900">1 год</strong></span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-purple-100 flex items-center justify-center mt-0.5">
                    <svg className="w-3 h-3 sm:w-4 sm:h-4 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                  </span>
                  <span className="text-sm sm:text-base text-gray-700">На распашные и подъемные секционные ворота предоставляется гарантия <strong className="text-gray-900">2 года</strong></span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-purple-100 flex items-center justify-center mt-0.5">
                    <svg className="w-3 h-3 sm:w-4 sm:h-4 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                  </span>
                  <span className="text-sm sm:text-base text-gray-700">На металлокаркасы зданий гарантия <strong className="text-gray-900">5 лет</strong>. Гарантия не распространяется на лакокрасочное покрытие.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-purple-100 flex items-center justify-center mt-0.5">
                    <svg className="w-3 h-3 sm:w-4 sm:h-4 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                  </span>
                  <span className="text-sm sm:text-base text-gray-700">На металлокаркасы зданий сухих отапливаемых помещений с учётом поддержания лакокрасочного покрытия предоставляется гарантия <strong className="text-gray-900">25 лет</strong>.</span>
                </li>
              </ul>
            </div>
          </TabsContent>

          <TabsContent value="delivery" className="mt-4 sm:mt-6">
            <div className="rounded-lg border border-slate-200 bg-white p-4 sm:p-6 shadow-sm">
              {isMoscow ? (
                <>
                  <p className="text-sm sm:text-base text-gray-700 mb-4 sm:mb-6">
                    Наша компания готова осуществить доставку приобретаемой у нас продукции по Москве и Московской области. В случае необходимости усилиями наших логистов может быть организована доставка продукции в любой регион России.
                  </p>
                  <p className="text-sm sm:text-base text-gray-700 mb-4 sm:mb-6">
                    По вопросам доставки обращайтесь к Нашим менеджерам по телефону: <a href={`tel:${branchPhoneHref}`} className="text-blue-600 hover:text-blue-800 font-medium">{branchPhone}</a>
                  </p>
                  <p className="text-sm sm:text-base text-gray-700 mb-4 sm:mb-6">
                    Обширный парк машин и применение попутных доставок позволяют снизить стоимость доставки продукции.
                  </p>
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4 sm:mb-6">
                    НАШ АВТОПАРК ДЛЯ ДОСТАВКИ ВАШИХ ЗАКАЗОВ
                  </h3>
                  <ul className="space-y-3 sm:space-y-4">
                    <li className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-orange-100 flex items-center justify-center mt-0.5">
                        <svg className="w-3 h-3 sm:w-4 sm:h-4 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                        </svg>
                      </span>
                      <span className="text-sm sm:text-base text-gray-700">Доставка по Москве микроавтобус <strong className="text-gray-900">3500 руб.</strong></span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-orange-100 flex items-center justify-center mt-0.5">
                        <svg className="w-3 h-3 sm:w-4 sm:h-4 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                        </svg>
                      </span>
                      <span className="text-sm sm:text-base text-gray-700">Доставка по Москве газель с "катюшей" 6м 1,2т или 4м 1,5т <strong className="text-gray-900">7500 руб.</strong></span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-orange-100 flex items-center justify-center mt-0.5">
                        <svg className="w-3 h-3 sm:w-4 sm:h-4 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                        </svg>
                      </span>
                      <span className="text-sm sm:text-base text-gray-700">Доставка по Москве Валдай 6м 5т <strong className="text-gray-900">10500 руб.</strong></span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-orange-100 flex items-center justify-center mt-0.5">
                        <svg className="w-3 h-3 sm:w-4 sm:h-4 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                        </svg>
                      </span>
                      <span className="text-sm sm:text-base text-gray-700">Доставка по Москве манимулятором 6м 3т <strong className="text-gray-900">19500 руб.</strong></span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-orange-100 flex items-center justify-center mt-0.5">
                        <svg className="w-3 h-3 sm:w-4 sm:h-4 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                        </svg>
                      </span>
                      <span className="text-sm sm:text-base text-gray-700">Доставка по Москве шаландой 12м 20т <strong className="text-gray-900">24500 руб.</strong></span>
                    </li>
                  </ul>
                </>
              ) : (
                <>
                  <p className="text-sm sm:text-base text-gray-700 mb-4 sm:mb-6">
                    Наша компания готова осуществить доставку приобретаемой у нас продукции по Санкт-Петербургу и Ленинградской области. В случае необходимости усилиями наших логистов может быть организована доставка продукции в любой регион России.
                  </p>
                  <p className="text-sm sm:text-base text-gray-700 mb-4 sm:mb-6">
                    По вопросам доставки обращайтесь к Нашим менеджерам по телефону: <a href={`tel:${branchPhoneHref}`} className="text-blue-600 hover:text-blue-800 font-medium">{branchPhone}</a>
                  </p>
                  <p className="text-sm sm:text-base text-gray-700 mb-4 sm:mb-6">
                    Обширный парк машин и применение попутных доставок позволяют снизить стоимость доставки продукции.
                  </p>
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4 sm:mb-6">
                    НАШ АВТОПАРК ДЛЯ ДОСТАВКИ ВАШИХ ЗАКАЗОВ
                  </h3>
                  <ul className="space-y-3 sm:space-y-4">
                    <li className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-orange-100 flex items-center justify-center mt-0.5">
                        <svg className="w-3 h-3 sm:w-4 sm:h-4 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                        </svg>
                      </span>
                      <span className="text-sm sm:text-base text-gray-700">Доставка по Санкт-Петербургу микроавтобус <strong className="text-gray-900">3500 руб.</strong></span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-orange-100 flex items-center justify-center mt-0.5">
                        <svg className="w-3 h-3 sm:w-4 sm:h-4 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                        </svg>
                      </span>
                      <span className="text-sm sm:text-base text-gray-700">Доставка по Санкт-Петербургу газель с "катюшей" 6м 1,2т или 4м 1,5т <strong className="text-gray-900">7500 руб.</strong></span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-orange-100 flex items-center justify-center mt-0.5">
                        <svg className="w-3 h-3 sm:w-4 sm:h-4 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                        </svg>
                      </span>
                      <span className="text-sm sm:text-base text-gray-700">Доставка по Санкт-Петербургу Валдай 6м 5т <strong className="text-gray-900">10500 руб.</strong></span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-orange-100 flex items-center justify-center mt-0.5">
                        <svg className="w-3 h-3 sm:w-4 sm:h-4 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                        </svg>
                      </span>
                      <span className="text-sm sm:text-base text-gray-700">Доставка по Санкт-Петербургу манимулятором 6м 3т <strong className="text-gray-900">19500 руб.</strong></span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-orange-100 flex items-center justify-center mt-0.5">
                        <svg className="w-3 h-3 sm:w-4 sm:h-4 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                        </svg>
                      </span>
                      <span className="text-sm sm:text-base text-gray-700">Доставка по Санкт-Петербургу шаландой 12м 20т <strong className="text-gray-900">24500 руб.</strong></span>
                    </li>
                  </ul>
                </>
              )}
            </div>
          </TabsContent>

          <TabsContent value="payment" className="mt-4 sm:mt-6">
            <div className="rounded-lg border border-slate-200 bg-white p-4 sm:p-6 shadow-sm">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4 sm:mb-6">
                Наличный и безналичный расчет
              </h3>
              <ul className="space-y-3 sm:space-y-4">
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-blue-100 flex items-center justify-center mt-0.5 font-semibold text-blue-700 text-xs sm:text-sm">
                    1
                  </span>
                  <span className="text-sm sm:text-base text-gray-700">Предоплата в офисе</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-blue-100 flex items-center justify-center mt-0.5 font-semibold text-blue-700 text-xs sm:text-sm">
                    2
                  </span>
                  <span className="text-sm sm:text-base text-gray-700">Безналичный расчет для организаций. 100% Предоплата или частичная отсрочка платежа через Сбер факторинг</span>
                </li>
              </ul>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </section>
  );
}
